import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert, Box, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Tab, Tabs, Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import Snackbar from "@plannel/components/Snackbar";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import scenarioService from "@plannel/services/ai/scenarioService";
import reduxUtil from "@plannel/utils/redux-util";
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";
import LlmUsageOverviewTab from "./llmUsage/LlmUsageOverviewTab";
import LlmUsageDetailTab from "./llmUsage/LlmUsageDetailTab";
import LlmUsagePriceTab from "./llmUsage/LlmUsagePriceTab";
import ProviderChip from "./llmUsage/ProviderChip";
import {
  FEATURES, MODULES, OPERATIONS, STATUSES, dateStr, featureLabel, moduleLabel, operationLabel, statusLabel,
} from "./llmUsage/llmUsageUtil";

// 관리자용 LLM 토큰 사용량 — 원장(z_ai_llm_usage) 집계와 단가 결합 추정 비용. 제공자 비종속 코드만 다룬다.
const TAB_OVERVIEW = 0;
const TAB_DETAIL = 1;
const TAB_PRICE = 2;
const FILTER_W = 150;

// 조회 중에는 같은 자리에서 스피너로 바꾼다(버튼 크기 유지)
const RefreshButton = ({ label, loading, onClick }) => (
  <Tooltip title={label}>
    <Box component="span">
      <IconButton aria-label={label} color="info" size="small" disabled={loading} onClick={onClick}>
        {loading ? (
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress color="inherit" size={18} />
          </Box>
        ) : <RefreshIcon />}
      </IconButton>
    </Box>
  </Tooltip>
);

const defaultFilters = () => {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 29);
  return { from_dt: dateStr(from), to_dt: dateStr(to), feature_cd: "", module_cd: "", operation_cd: "",
    model_id: "", user_id: "", status_cd: "" };
};

// YYYY-MM-DD → 로컬 자정 Date(UTC 로 해석되면 음수 시차에서 하루 밀린다)
const localDate = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new ZDate(y, m - 1, d);
};

const AiLlmUsage = ({ viewName }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  // viewState 는 객체로 중첩 저장 — 최상위 숫자형 문자열은 복원 시 BigInt 로 바뀔 수 있다
  const saved = reduxUtil.getViewState(viewName);

  const [tab, setTab] = useState(saved?.ui?.tab ?? TAB_OVERVIEW);
  // 내역 축·펼침은 MDI 탭이 URL 을 공유하므로 URL 이 아니라 viewState 에 둔다
  const [breakdown, setBreakdown] = useState(saved?.ui?.breakdown ?? "feature");
  const [expanded, setExpanded] = useState(!!saved?.ui?.expanded);
  const [applied, setApplied] = useState(() => ({ ...defaultFilters(), ...(saved?.filters || {}) }));
  // 사용자 검색어는 TextFieldSearch 규약대로 ref 로 들고 Enter·검색 아이콘에서만 반영(로그인ID·표시명·성명 부분일치)
  const userSearch = useRef(applied.user_id || "");
  const [period, setPeriod] = useState(() => ({ start: localDate(applied.from_dt), end: localDate(applied.to_dt) }));
  const [reloadKey, setReloadKey] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [modelMeta, setModelMeta] = useState({});
  const [snack, setSnack] = useState({ open: false, severity: "", content: "" });
  // 탭 행 오른쪽 자리 — 명세·단가 탭이 자기 버튼을 포털로 그려 넣는다
  const [tabToolbar, setTabToolbar] = useState(null);

  const notify = (severity, content) => setSnack({ open: true, severity, content });

  const loadSummary = async (f) => {
    setLoading(true);
    try {
      setSummary(await scenarioService.llmUsageSummary(f));
    } catch {
      setSummary(null);
      notify("error", t("agentTraceLoadFail"));
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      const r = await scenarioService.llmUsageModels();
      // 필터 값은 모델 ID — 같은 ID 를 여러 제공사가 쓰면 제공사를 모아 둔다(이름은 있는 쪽을 쓴다)
      const meta = {};
      (r?.models || []).forEach((m) => {
        const e = meta[m.model_id] || (meta[m.model_id] = { label: m.model_id, providers: [] });
        if (m.model_nm) e.label = m.model_nm;
        if (m.provider_cd && !e.providers.includes(m.provider_cd)) e.providers.push(m.provider_cd);
      });
      setModels(Object.keys(meta).sort((a, b) => a.localeCompare(b)));
      setModelMeta(meta);
    } catch {
      setModels([]); setModelMeta({});
    }
  };

  useEffect(() => {
    loadSummary(applied);
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  useEffect(() => {
    reduxDispatch(reduxUtil.updateViewState(
      reduxUtil.createObjectState(viewName, { filters: applied, ui: { tab, breakdown, expanded } })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, tab, breakdown, expanded]);

  const applyFilters = (patch) => {
    setApplied((prev) => ({ ...prev, ...patch }));
    setReloadKey((k) => k + 1);
  };
  const applyFilter = (key, value) => applyFilters({ [key]: value ?? "" });

  // PeriodFilter 는 마운트 때도 setPeriod 를 부르므로 날짜가 실제로 바뀐 경우만 조회한다
  useEffect(() => {
    if (!period?.start || !period?.end) return;
    const from = dateStr(period.start);
    const to = dateStr(period.end);
    if (from !== applied.from_dt || to !== applied.to_dt) applyFilters({ from_dt: from, to_dt: to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const searchUser = () => applyFilter("user_id", userSearch.current.trim());

  // 개요의 오류 수 클릭 — 나머지 조건은 그대로 두고 상태만 오류로 고정해 상세 탭으로 옮긴다
  const goErrors = () => {
    applyFilters({ status_cd: "error" });
    setTab(TAB_DETAIL);
  };

  const changeAxis = (axis) => {
    setBreakdown(axis);
    setExpanded(false);
  };

  // 같은 표시명이 여러 제공사에 걸치면 제공사 칩으로 구분한다(개요 내역과 같은 규칙)
  const providersByLabel = useMemo(() => {
    const out = {};
    Object.values(modelMeta).forEach((m) => {
      out[m.label] = [...new Set([...(out[m.label] || []), ...m.providers])];
    });
    return out;
  }, [modelMeta]);

  const modelOptionLabel = (id) => {
    const m = modelMeta[id];
    if (!m) return id;
    if ((providersByLabel[m.label] || []).length < 2) return m.label;
    return (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{m.label}</Box>
        {m.providers.map((p) => <ProviderChip key={p} provider={p} />)}
      </Box>
    );
  };

  const selectFilter = (key, label, options, render, width = FILTER_W) => {
    return (
      <FormControl size="small" sx={{ width }}>
        <InputLabel shrink>{label}</InputLabel>
        <Select value={applied[key]} label={label} displayEmpty notched
          onChange={(e) => applyFilter(key, e.target.value)}>
          <MenuItem value="">{t("agentTraceAllModules")}</MenuItem>
          {options.map((o) => <MenuItem key={o} value={o}>{render(o)}</MenuItem>)}
        </Select>
      </FormControl>
    );
  };

  return (
    <Box sx={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 1.5, flexShrink: 0 }}>
        <FilterContainer sx={{ columnGap: 1 }}>
          <Box sx={{ display: "flex" }}>
            <PeriodFilter viewName={viewName} period={period} setPeriod={setPeriod} />
          </Box>
          {selectFilter("feature_cd", t("llmUsageFeature"), FEATURES, (o) => featureLabel(t, o))}
          {selectFilter("module_cd", t("agentTraceModule"), MODULES, (o) => moduleLabel(t, o))}
          {selectFilter("operation_cd", t("llmUsageOperation"), OPERATIONS, (o) => operationLabel(t, o))}
          {/* 값은 모델 ID, 보이는 글자는 표시명이 있으면 이름(겹치면 제공사 칩) */}
          {selectFilter("model_id", t("agentTraceMetricModel"), models, modelOptionLabel, 220)}
          <TextFieldSearch sx={{ width: FILTER_W }} label="user" searchCode={userSearch} retrieve={searchUser} />
          {/* 상태는 호출 명세에만 걸린다 — 개요 집계 API 가 받지 않아 다른 탭에서는 내보내지 않는다.
              FilterContainer 는 자식마다 칸을 만들어 빈 칸도 간격을 남기므로, 사라져도 티가 안 나게 맨 뒤에 둔다 */}
          {tab === TAB_DETAIL && selectFilter("status_cd", t("status"), STATUSES, (o) => statusLabel(t, o), 110)}
          <RefreshButton label={t("agentTraceRefresh")} loading={loading} onClick={() => setReloadKey((k) => k + 1)} />
        </FilterContainer>
      </Box>

      {summary?.tables_missing && (
        <Alert severity="info" sx={{ fontSize: FONT.BODY, py: 0, mx: 1.5, mb: 1, flexShrink: 0 }}>{t("llmUsageTablesMissing")}</Alert>
      )}

      <Box sx={{ px: 1.5, display: "flex", alignItems: "center", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 34, flex: 1 }}>
          <Tab label={t("overview")} sx={{ minHeight: 34, fontSize: FONT.SUB }} />
          <Tab label={t("llmUsageTabDetail")} sx={{ minHeight: 34, fontSize: FONT.SUB }} />
          <Tab label={t("llmUsageTabPrice")} sx={{ minHeight: 34, fontSize: FONT.SUB }} />
        </Tabs>
        <Box ref={setTabToolbar} sx={{ display: "flex", alignItems: "center" }} />
      </Box>

      <Box key={tab} sx={{ flex: 1, minHeight: 0, overflow: "hidden",
        ...(tab === TAB_OVERVIEW ? null : { p: 1.5 }) }}>
        {tab === TAB_OVERVIEW && (
          <LlmUsageOverviewTab summary={summary} loading={loading} onGoPrices={() => setTab(TAB_PRICE)}
            onGoErrors={goErrors}
            breakdownProps={{
              axis: breakdown, onChangeAxis: changeAxis, expanded, onExpand: () => setExpanded(true),
            }} />
        )}
        {tab === TAB_DETAIL && (
          <LlmUsageDetailTab viewName={viewName} filters={applied} reloadKey={`${reloadKey}`} notify={notify}
            toolbarEl={tabToolbar} />
        )}
        {tab === TAB_PRICE && (
          <LlmUsagePriceTab viewName={viewName} reloadKey={reloadKey} notify={notify}
            onSaved={() => { loadSummary(applied); loadModels(); }} toolbarEl={tabToolbar} />
        )}
      </Box>

      <Snackbar open={snack.open} onClose={() => setSnack({ ...snack, open: false })}
        severity={snack.severity} content={snack.content} />
    </Box>
  );
};

export default AiLlmUsage;
